import React, { useState, useEffect } from 'react';
import { Calendar, Copy, Check, ExternalLink, Loader2, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarSyncModal = ({ isOpen, onClose }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncData, setSyncData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionUrl();
    }
  }, [isOpen]);

  const fetchSubscriptionUrl = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}/api/calendar-sync/subscription-url`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get subscription URL');
      }
      
      const data = await response.json();
      setSyncData(data);
      
      if (!data.success) {
        setError(data.message || 'Unable to generate subscription URL');
      }
    } catch (err) {
      console.error('Error fetching subscription URL:', err);
      setError('Failed to load calendar sync information');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (syncData?.subscription_url) {
      try {
        await navigator.clipboard.writeText(syncData.subscription_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Calendar className="w-6 h-6 text-yellow-500 mr-3" />
            <h2 className="text-xl font-bold text-gray-800">Sync with Calendar</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="close-sync-modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Unable to Sync</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          ) : syncData?.success ? (
            <>
              {/* Calendar Info */}
              {syncData.calendar_name && (
                <div className="mb-4">
                  <span className="text-sm text-gray-500">Calendar:</span>
                  <span className="ml-2 font-medium text-gray-800">{syncData.calendar_name}</span>
                </div>
              )}

              {/* Subscription URL */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subscription URL
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    readOnly
                    value={syncData.subscription_url}
                    className="flex-1 p-3 bg-gray-100 border border-gray-300 rounded-l-lg text-sm text-gray-700 font-mono truncate"
                    data-testid="sync-url-input"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-4 py-3 rounded-r-lg transition-colors flex items-center ${
                      copied 
                        ? 'bg-green-500 text-white' 
                        : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    }`}
                    data-testid="copy-sync-url"
                  >
                    {copied ? (
                      <>
                        <Check className="w-5 h-5 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">How to Subscribe</h3>
                <ol className="space-y-2">
                  {syncData.instructions?.map((instruction, idx) => (
                    <li key={idx} className="flex items-start text-sm text-blue-700">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center text-blue-800 font-semibold mr-2">
                        {idx + 1}
                      </span>
                      <span className="pt-0.5">{instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Quick Links */}
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 mb-3">Quick add to:</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(syncData.subscription_url)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <img 
                      src="https://www.gstatic.com/images/branding/product/1x/calendar_48dp.png" 
                      alt="Google Calendar" 
                      className="w-5 h-5 mr-2"
                    />
                    Google Calendar
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                  <a
                    href={`webcal://${syncData.subscription_url.replace('https://', '').replace('http://', '')}`}
                    className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg mr-2">🍎</span>
                    Apple Calendar
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>

              {/* Note */}
              <p className="mt-4 text-xs text-gray-500">
                ℹ️ Calendar apps typically refresh subscriptions every 15-60 minutes. 
                New appointments may take some time to appear.
              </p>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Unable to load sync information.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarSyncModal;
